import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { GlowBackground } from '../components/GlowBackground';
import { AnimatedText } from '../components/AnimatedText';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

const AITool: React.FC<{
  icon: string;
  title: string;
  desc: string;
  delay: number;
  side: 'left' | 'right';
}> = ({ icon, title, desc, delay, side }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;

  if (f < 0) return null;

  const scale = spring({ frame: f, fps, config: { damping: 15, stiffness: 100 } });
  const opacity = interpolate(f, [0, 15], [0, 1], { extrapolateRight: 'clamp' });
  const x = interpolate(f, [0, 20], [side === 'left' ? -50 : 50, 0], {
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        padding: '24px 32px',
        background: `linear-gradient(135deg, ${COLORS.bgLight}DD, ${COLORS.bgLight}88)`,
        border: `1px solid ${COLORS.violet}30`,
        borderRadius: 16,
        opacity,
        transform: `translateX(${x}px) scale(${scale})`,
        width: 450,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: `linear-gradient(135deg, ${COLORS.violet}30, ${COLORS.primary}20)`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: 28,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: COLORS.white,
            fontFamily: 'Inter, system-ui, sans-serif',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 14,
            color: COLORS.gray,
            fontFamily: 'Inter, system-ui, sans-serif',
            lineHeight: 1.4,
          }}
        >
          {desc}
        </div>
      </div>
    </div>
  );
};

const DUR = SCENES.AI.duration;

export const AIScene: React.FC = () => {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 15, DUR], [1, 0], { extrapolateRight: 'clamp' });

  const nodes = Array.from({ length: 12 }, (_, i) => {
    const layer = Math.floor(i / 4);
    const pos = i % 4;
    const x = 960 + (layer - 1) * 180;
    const y = 300 + pos * 80 + (layer === 1 ? 40 : 0);
    const pulse = Math.sin(frame * 0.1 + i) * 0.3 + 0.7;
    return { x, y, pulse };
  });

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut }}>
      <GlowBackground variant="dramatic" />

      <svg
        style={{
          position: 'absolute',
          inset: 0,
          opacity: interpolate(frame, [20, 50], [0, 0.3], {
            extrapolateRight: 'clamp',
            extrapolateLeft: 'clamp',
          }),
        }}
        viewBox="0 0 1920 1080"
      >
        {nodes.map((from, i) =>
          nodes
            .filter((_, j) => Math.floor(j / 4) === Math.floor(i / 4) + 1)
            .map((to, j) => (
              <line
                key={`${i}-${j}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={COLORS.primary}
                strokeWidth={1}
                opacity={from.pulse * 0.5}
              />
            ))
        )}
      </svg>

      {nodes.map((node, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: node.x - 6,
            top: node.y - 6,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: COLORS.violet,
            opacity: interpolate(frame, [20, 40], [0, node.pulse], {
              extrapolateRight: 'clamp',
              extrapolateLeft: 'clamp',
            }),
            boxShadow: `0 0 12px ${COLORS.violet}`,
          }}
        />
      ))}

      <div style={{ position: 'absolute', top: 40, left: 0, right: 0 }}>
        <AnimatedText
          text="IA en Cada Decisión"
          fontSize={56}
          fontWeight={800}
          mode="scaleIn"
          startFrame={5}
          style={{
            background: COLORS.gradientViolet,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 80,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 20,
        }}
      >
        <AITool
          icon="🎯"
          title="Simulador"
          desc="Escenarios what-if con impacto en margen y precio"
          delay={25}
          side="left"
        />
        <AITool
          icon="📈"
          title="Predicciones"
          desc="Probabilidad de ganar cotizaciones"
          delay={55}
          side="left"
        />
      </div>

      <div
        style={{
          position: 'absolute',
          right: 80,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 20,
          alignItems: 'flex-end',
        }}
      >
        <AITool
          icon="💡"
          title="Recomendaciones"
          desc="Precios óptimos basados en datos históricos"
          delay={85}
          side="right"
        />
        <AITool
          icon="🔍"
          title="Insights"
          desc="Anomalías, tendencias y rankings automáticos"
          delay={115}
          side="right"
        />
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 500,
          opacity: interpolate(frame, [140, 160], [0, 1], {
            extrapolateRight: 'clamp',
            extrapolateLeft: 'clamp',
          }),
        }}
      >
        <div
          style={{
            background: `${COLORS.bgLight}EE`,
            border: `1px solid ${COLORS.violet}30`,
            borderRadius: 16,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: COLORS.gradientViolet,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              🤖
            </div>
            <div
              style={{
                fontSize: 14,
                color: COLORS.gray,
                fontFamily: 'Inter, system-ui, sans-serif',
                lineHeight: 1.5,
              }}
            >
              {(() => {
                const chatText =
                  'Basándome en las últimas 50 cotizaciones aprobadas, recomiendo un precio de $2,450 con un margen del 32%.';
                const charsToShow = Math.floor(
                  interpolate(frame - 170, [0, 60], [0, chatText.length], {
                    extrapolateLeft: 'clamp',
                    extrapolateRight: 'clamp',
                  })
                );
                return chatText.slice(0, charsToShow);
              })()}
            </div>
          </div>
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.AI} />
    </AbsoluteFill>
  );
};
